import { Injectable, Injector } from '@angular/core';
import { HttpClient, HttpHeaders, HttpUrlEncodingCodec } from '@angular/common/http';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

import { isNullOrEmpty } from '@awp/core/utils';
import { SirenAction, SirenLink, Parameters, SirenResponse } from '../model';
import { RESPONSE_LOGGER_TOKEN, ResponseLogger } from '../response-console.logger';
import { ROUTE_PARAM_REGEX } from './siren-http.service';
import { SirenCache } from './siren-cache';


@Injectable({
  providedIn: 'root'
})
export class SirenClient {

  private _headers = new HttpHeaders({ 'Content-Type': 'application/json' });
  private _encoder = new HttpUrlEncodingCodec();
  private _httpClient: HttpClient;
  private _injector: Injector;

  constructor(parentInjector: Injector) {
    this._injector = parentInjector;
  }

  private getClient(): HttpClient {
    if (this._httpClient == null) {
      this._httpClient = this._injector.get(HttpClient);
    }

    return this._httpClient;
  }

  public get<T extends SirenResponse>(link: SirenLink | string, param?: Parameters): Observable<T> {
    if (link == null) {
      throw new Error('Link is not defined');
    }

    const sirenLink = this.resolveSirenLink(link);
    const loggers = <ResponseLogger[]>(this._injector.get(RESPONSE_LOGGER_TOKEN) as [] || []);
    const parsedUrl = this.parseUrlRouteParams(sirenLink.href, param ? param.route : null);
    const cache = this.cache;

    const rel = sirenLink.rel != null ? sirenLink.rel[0].replace('rel/', ''): null;
    const linkHeaders = rel != null ? this._headers.set('Siren', rel) : this._headers;

    return this.getClient().get<T>(parsedUrl, {
        headers: linkHeaders,
        params: param != null ? param.queryParams : null,
      })
      .pipe(
        tap(response => {
          cache.setResponse(response.rel[0], response);
        }),
        tap(response => {
          loggers.forEach(logger => {
            logger.log(parsedUrl, response);
          });
        })
      );
  }

  public getBlob(link: SirenLink | string, param?: Parameters): Observable<Blob> {
    if (link == null) {
      throw new Error('Link is not defined');
    }

    const url = (typeof link === 'string') ? link : link.href;
    const parsedUrl = this.parseUrlRouteParams(url, param ? param.route : null);

    return this._httpClient.get(parsedUrl, { headers: this._headers, responseType: 'blob' });
  }

  public post<TRequest, TResponse extends SirenResponse>(action: SirenAction | string, request: TRequest, param?: Parameters): Observable<TResponse> {
    if (action == null) {
      throw new Error('Action is not defined');
    }

    const sirenAction = this.resolveActionUrl(action);
    const loggers = <ResponseLogger[]>(this._injector.get(RESPONSE_LOGGER_TOKEN) as [] || []);
    const parsedUrl = this.parseUrlRouteParams(sirenAction.href, param ? param.route : null);
    const cache = this.cache;
    const actionHeaders = sirenAction.name != null ? this._headers.set('Siren', sirenAction.name) : this._headers;

    return this.getClient().request<TResponse>(sirenAction.method || 'POST', parsedUrl, {
        body: request,
        params: param != null ? param.queryParams : null,
        headers: actionHeaders
      })
      .pipe(
        tap(response => {
          cache.setResponse(response.rel[0], response);
        }),
        tap(response => {
          loggers.forEach(logger => {
            logger.log(parsedUrl, response);
          });
        }),
      );
  }

  private parseUrlRouteParams(url: string, params: any): string {
    if (params == null) return url;

    const result = url.replace(ROUTE_PARAM_REGEX, (match, key) => {
      const value = params[key];
      const encodedValue = isNullOrEmpty(value) ? value : this._encoder.encodeValue(value);
      return encodedValue;
    });

    return result;
  }

  private _cache: SirenCache;
  get cache(): SirenCache {
    if (this._cache == null) {
      this._cache = this._injector.get<SirenCache>(SirenCache);
    }

    return this._cache;
  }

  public getAction(name: string): SirenAction {
    return this.cache.getAction(name);
  }

  public getResponse(name: string): SirenResponse {
    return this.cache.getResponse(name);
  }

  public getLink(name: string): SirenLink {
    return this.cache.getLink(name);
  }

  private resolveSirenLink(value: SirenLink | string ): SirenLink {
    let result: SirenLink;
    if (typeof value === 'string') {
      if (!value.startsWith('http')) {
        const linkRef = this.getLink(value); // resolve the link name
        if (linkRef == null) {
          throw new Error('Link not found');
        }
        result = linkRef;
      } else {
        result = { href: value, rel: ['unknown'] };
      }
    } else {
      result = value;
    }

    return result;
  }

  private resolveActionUrl(value: SirenAction | string ): SirenAction {
    let result: SirenAction;
    if (typeof value === 'string') {
      if (!value.startsWith('http')) {
        const actionRef = this.getAction(value); // resolve the action name
        if (actionRef == null) {
          throw new Error('Action not found');
        }
        result = actionRef;
      } else {
        result = { href: value, method: 'POST', name: 'unknown' }; // Assume POST
      }
    } else {
      result = value;
    }

    return result;
  }

}
